/**
 * Formatting Utilities
 * Centralized functions for currency, numbers, and other display formatting
 * 
 * Usage:
 *   import { formatCurrency, formatAmount, formatPoints } from '@/utils/formatters';
 */

import { getHomeCurrency } from './currencies';

/**
 * Format currency amount with proper symbol and locale formatting
 * 
 * @param {number} amount - The amount to format
 * @param {string} [currency] - Currency code (USD, EUR, GBP, etc.). Defaults
 *                              to the user's `home_currency` setting when
 *                              omitted, since most call sites that don't
 *                              pass a currency are rendering trip totals
 *                              already converted into the home currency.
 * @param {object} options - Formatting options
 * @param {boolean} options.showSymbol - Show currency symbol (default: true)
 * @param {boolean} options.showCode - Show currency code (default: false for home, true for others)
 * @param {number} options.decimals - Number of decimal places (default: 2)
 * @param {boolean} options.compact - Use compact notation for large numbers (default: false)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency, options = {}) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return options.fallback || '-';
  }

  // Default to the user's configured home currency when none was passed —
  // matches how the backend stores `base_currency_amount`.
  const resolvedCurrency = currency || getHomeCurrency();

  const {
    showSymbol = true,
    showCode = resolvedCurrency !== getHomeCurrency(),
    decimals = 2,
    compact = false
  } = options;
  
  // Currency symbols
  const symbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥',
    CAD: 'C$', AUD: 'A$', CHF: 'CHF ', INR: '₹',
    KRW: '₩', SGD: 'S$', HKD: 'HK$', MXN: 'MX$',
    BRL: 'R$', ZAR: 'R', NZD: 'NZ$', SEK: 'kr',
    NOK: 'kr', DKK: 'kr', TRY: '₺', THB: '฿'
  };
  
  const symbol = symbols[resolvedCurrency] || '';
  
  // Format the number
  let formatted;
  if (compact && Math.abs(amount) >= 1000) {
    if (Math.abs(amount) >= 1000000) {
      formatted = (amount / 1000000).toFixed(1) + 'M';
    } else if (Math.abs(amount) >= 1000) {
      formatted = (amount / 1000).toFixed(1) + 'K';
    }
  } else {
    formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }
  
  // Build the output string
  let result = '';
  if (showSymbol && symbol) {
    result = symbol + formatted;
  } else {
    result = formatted;
  }
  
  if (showCode) {
    result += ` ${resolvedCurrency}`;
  }
  
  return result;
};

/**
 * Format amount in USD (shorthand for common use case)
 * 
 * @param {number} amount - The amount to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted USD string (e.g., "$1,234.56")
 */
export const formatUSD = (amount, options = {}) => {
  return formatCurrency(amount, 'USD', { showCode: false, ...options });
};

/**
 * Format amount with currency conversion display
 * Shows original amount with USD equivalent
 * 
 * @param {number} amount - Original amount
 * @param {string} currency - Original currency
 * @param {number} amountUSD - Amount in USD (if converted)
 * @returns {string} Formatted string showing both amounts
 */
export const formatWithConversion = (amount, currency, amountUSD) => {
  if (!amount || isNaN(amount)) return '-';
  
  const original = formatCurrency(amount, currency);
  
  if (currency === 'USD' || !amountUSD || amountUSD === amount) {
    return original;
  }
  
  const converted = formatCurrency(amountUSD, 'USD', { showCode: false });
  return `${original} (${converted})`;
};

/**
 * Format loyalty points
 * 
 * @param {number} points - Number of points
 * @param {string} programName - Optional program name suffix
 * @returns {string} Formatted points string (e.g., "125,053 pts")
 */
export const formatPoints = (points, programName = '') => {
  if (!points || points === 0 || isNaN(points)) {
    return '-';
  }
  
  const formatted = Math.round(points).toLocaleString('en-US');
  const suffix = programName ? ` ${programName}` : ' pts';
  
  return `${formatted}${suffix}`;
};

/**
 * Format percentage
 * 
 * @param {number} value - The percentage value (0-100 or 0-1)
 * @param {object} options - Formatting options
 * @param {boolean} options.isDecimal - If true, value is 0-1 instead of 0-100
 * @param {number} options.decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercent = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  
  const { isDecimal = false, decimals = 1 } = options;
  const percent = isDecimal ? value * 100 : value;
  
  return `${percent.toFixed(decimals)}%`;
};

/**
 * Format a number with thousands separators
 * 
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Format file size in human-readable format
 * 
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  
  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
};

/**
 * Parse currency string back to number
 * Handles various formats: "$1,234.56", "1234.56 USD", "€1.234,56"
 * 
 * @param {string} str - Currency string to parse
 * @returns {number|null} Parsed number or null if invalid
 */
export const parseCurrency = (str) => {
  if (!str) return null;
  
  // Remove currency symbols and codes
  const cleaned = str
    .replace(/[^0-9.,-]/g, '')
    .replace(/,(?=\d{3})/g, ''); // Remove thousands separators
  
  const number = parseFloat(cleaned);
  return isNaN(number) ? null : number;
};
